/**
 * useCirurgiasTab - Hook for CirurgiasTab state and handlers
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import cirurgiasFaturarService, {
  type ItemCirurgiaFaturar,
} from '../services/cirurgiasFaturarService';
import type { Cliente } from '../services/clienteService';
import { tsvToXlsxFile, parseTsv } from '../utils/pasteToXlsx';

interface UseCirurgiasTabOptions {
  jobId: number;
  jobClientes: Cliente[];
  globalClienteFilter?: (string | number)[];
}

export function useCirurgiasTab({
  jobId,
  jobClientes,
  globalClienteFilter,
}: UseCirurgiasTabOptions) {
  const [cirurgias, setCirurgias] = useState<ItemCirurgiaFaturar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [hideLotsAndValidity, setHideLotsAndValidity] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [addingItem, setAddingItem] = useState(false);

  // Modal states
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Import modal states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [selectedImportClient, setSelectedImportClient] = useState<number | null>(null);
  const [selectedRegime, setSelectedRegime] = useState<boolean | null>(null);
  // T26 (#15): Colar do Excel — mesmo padrão dos modais da base
  const [importMode, setImportMode] = useState<'arquivo' | 'colar'>('arquivo');
  const [pastedText, setPastedText] = useState('');
  const pastedRows = useMemo(() => parseTsv(pastedText).length, [pastedText]);

  const loadCirurgias = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await cirurgiasFaturarService.getAllItems(jobId);
      setCirurgias(items);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar cirurgias');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadCirurgias();
  }, [loadCirurgias]);

  const filteredCirurgias = useMemo(() => {
    if (!globalClienteFilter || globalClienteFilter.length === 0) {
      return cirurgias;
    }
    const selectedNames = new Set<string>();
    globalClienteFilter.forEach(id => {
      const cliente = jobClientes.find(c => c.id === Number(id));
      if (cliente) {
        if (cliente.nome_fantasia) selectedNames.add(cliente.nome_fantasia);
        if (cliente.razao_social) selectedNames.add(cliente.razao_social);
      }
    });
    return cirurgias.filter(item =>
      item.cliente_nome_fantasia && selectedNames.has(item.cliente_nome_fantasia)
    );
  }, [cirurgias, globalClienteFilter, jobClientes]);

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      await cirurgiasFaturarService.downloadAndSaveExcel(jobId);
    } catch (err: any) {
      setError(err.message || 'Erro ao baixar Excel');
    } finally {
      setDownloading(false);
    }
  };

  const handleUploadExcel = async () => {
    // T26: no modo "colar", converte o TSV do clipboard em .xlsx em memória
    // e envia pelo MESMO endpoint do upload (validação idêntica no backend).
    // Diferente dos modais da base, o endpoint de cirurgias casa colunas
    // pelo NOME do cabeçalho — o paste PRECISA incluir a linha de cabeçalho.
    let fileToSend: File | null = selectedFile;
    if (importMode === 'colar') {
      fileToSend = tsvToXlsxFile(pastedText, `cirurgias_${jobId}.xlsx`);
      if (!fileToSend) {
        setError('Nada para importar: cole as células do Excel (com a linha de cabeçalho).');
        return;
      }
    }
    if (!fileToSend) return;
    try {
      setUploading(true);
      setError(null);

      let clienteId: number | undefined;
      if (jobClientes.length === 1) {
        clienteId = jobClientes[0].id;
      } else if (selectedImportClient) {
        clienteId = selectedImportClient;
      }

      let comRegime: boolean | undefined;
      if (selectedRegime !== null) {
        comRegime = selectedRegime;
      } else if (clienteId) {
        const client = jobClientes.find(c => c.id === clienteId);
        if (client) {
          if (client.com_regime && !client.sem_regime) {
            comRegime = true;
          } else if (!client.com_regime && client.sem_regime) {
            comRegime = false;
          }
        }
      }

      const result = await cirurgiasFaturarService.uploadExcel(
        jobId,
        fileToSend,
        clienteId,
        comRegime
      );
      setUploadResult(result);
      await loadCirurgias();
      if (result.total_erros === 0) {
        setTimeout(() => closeImportModal(), 2000);
      }
    } catch (err: any) {
      const errorData = err.response?.data || {};
      const errorMessage = errorData.error || err.message || 'Erro ao fazer upload do Excel';
      setError(errorMessage);
      if (errorData.erros && errorData.erros.length > 0) {
        setUploadResult({
          total_linhas: 0,
          total_processadas: 0,
          total_erros: errorData.total_erros || errorData.erros.length,
          erros: errorData.erros,
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setSelectedFile(null);
    setUploadResult(null);
    setSelectedImportClient(null);
    setSelectedRegime(null);
    setError(null);
    // T26: reset do modo colar
    setImportMode('arquivo');
    setPastedText('');
  };

  const handleAddCirurgia = async (data: any) => {
    setAddingItem(true);
    try {
      await cirurgiasFaturarService.addItem(jobId, data);
      await loadCirurgias();
      setShowAddModal(false);
    } catch (err: any) {
      throw new Error(err.message || 'Erro ao adicionar cirurgia');
    } finally {
      setAddingItem(false);
    }
  };

  const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
    try {
      await cirurgiasFaturarService.updateItem(jobId, itemId, { quantidade: newQuantity });
      setCirurgias(prev => prev.map(item =>
        item.id === itemId
          ? { ...item, quantidade: newQuantity.toString() }
          : item
      ));
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar quantidade');
      await loadCirurgias();
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (deletingId === itemId) return;
    try {
      setDeletingId(itemId);
      await cirurgiasFaturarService.deleteItem(jobId, itemId);
      setCirurgias(prev => prev.filter(item => item.id !== itemId));
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir item');
    } finally {
      setDeletingId(null);
    }
  };

  return {
    // State
    cirurgias,
    loading,
    error,
    setError,
    downloading,
    hideLotsAndValidity,
    setHideLotsAndValidity,
    deletingId,
    addingItem,
    // Modal states
    showImportModal,
    setShowImportModal,
    showAddModal,
    setShowAddModal,
    showInfoModal,
    setShowInfoModal,
    // Import modal states
    selectedFile,
    setSelectedFile,
    uploading,
    uploadResult,
    selectedImportClient,
    setSelectedImportClient,
    selectedRegime,
    setSelectedRegime,
    // T26: Colar do Excel
    importMode,
    setImportMode,
    pastedText,
    setPastedText,
    pastedRows,
    // Derived
    filteredCirurgias,
    // Handlers
    loadCirurgias,
    handleDownloadExcel,
    handleUploadExcel,
    closeImportModal,
    handleAddCirurgia,
    handleUpdateQuantity,
    handleDeleteItem,
  };
}
